import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowUpRight,
  Layers,
  Box,
  Pill,
  Tablets,
  FlaskConical,
  Beaker,
  Package,
  Zap,
  Tag,
  Wind,
  Cog,
  ShieldCheck,
  Wrench,
} from "lucide-react";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { QuoteDialog } from "./QuoteDialog";
import { StickyMobileCTA } from "./StickyMobileCTA";
import { Toaster } from "../ui/sonner";
import { MACHINERY, MACHINERY_NOTE } from "../../lib/machinery";

const ICONS = {
  Layers,
  Box,
  Pill,
  Tablets,
  FlaskConical,
  Beaker,
  Package,
  Zap,
  Tag,
  Wind,
};

const CATEGORIES = [
  "All",
  "Granulation",
  "Drying",
  "Blending",
  "Capsule Line",
  "Tablet Line",
];

export const MachineryPage = () => {
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [active, setActive] = useState("All");
  const openQuote = () => setQuoteOpen(true);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const filtered =
    active === "All"
      ? MACHINERY
      : MACHINERY.filter((m) => m.category === active);

  return (
    <div className="App bg-[#FAFAFA] text-[#1A1A1A] min-h-screen font-body pb-16 lg:pb-0 overflow-x-hidden">
      <Header onQuoteClick={openQuote} />

      <main>
        {/* Hero */}
        <section
          data-testid="machinery-hero"
          className="relative pt-32 md:pt-40 pb-16 md:pb-24 bg-white border-b border-[#E5E5E5] overflow-hidden"
        >
          <div
            className="absolute inset-y-0 right-0 w-1/2 opacity-[0.06] pointer-events-none"
            style={{
              backgroundImage:
                "url(https://static.prod-images.emergentagent.com/jobs/90b64be9-35e0-4ce5-b8f4-3f206a5d709a/images/f04e8081ff75767fb66e7bafd8c5617b9ab4142eaed8e680bddd0a827757ce29.png)",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
            aria-hidden
          />
          <div className="relative max-w-7xl mx-auto px-6 md:px-10">
            <Link
              to="/"
              data-testid="machinery-back-link"
              className="inline-flex items-center gap-2 text-sm text-[#595959] hover:text-[#D4AF37] transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> Back to home
            </Link>

            <div className="mt-8 grid lg:grid-cols-12 gap-10 items-end">
              <div className="lg:col-span-8">
                <div className="overline">
                  <span className="gold-divider mr-3 align-middle" />
                  Inside our facility
                </div>
                <h1
                  data-testid="machinery-title"
                  className="font-serif-display text-4xl sm:text-5xl lg:text-[4.25rem] text-[#1A1A1A] mt-6 leading-[1.05] max-w-3xl"
                >
                  Our machinery —
                  <br />
                  built for steady output.
                </h1>
                <p className="mt-6 text-base md:text-lg text-[#595959] max-w-2xl leading-relaxed">
                  A look at the production equipment we operate across blending,
                  capsule, tablet, bottling, powder-filling and packaging
                  lines. Detailed specifications and photographs are added as
                  the catalogue is finalised.
                </p>
              </div>
              <div className="lg:col-span-4 flex lg:justify-end">
                <button
                  data-testid="machinery-quote-cta"
                  onClick={openQuote}
                  className="btn-gold rounded-sm h-12 px-7 text-sm font-semibold tracking-wider inline-flex items-center gap-2"
                >
                  Discuss Your Order <ArrowUpRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Stats strip */}
            <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-px bg-[#E5E5E5] border border-[#E5E5E5]">
              {[
                { label: "Production lines", value: "Capsule · Tablet · Powder" },
                { label: "Packaging formats", value: "Bottle · Pouch · Sachet" },
                { label: "Quality standards", value: "FSSAI · GMP · AYUSH" },
                { label: "Operating hours", value: "Mon – Sat · 9 to 6" },
              ].map((s) => (
                <div key={s.label} className="bg-white px-5 py-5">
                  <div className="overline text-[10px]">{s.label}</div>
                  <div className="font-serif-display text-base md:text-lg text-[#1A1A1A] mt-2">
                    {s.value}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Filter + grid */}
        <section className="py-20 md:py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
              <div>
                <div className="overline">
                  <span className="gold-divider mr-3 align-middle" />
                  Equipment catalogue
                </div>
                <h2 className="font-serif-display text-3xl md:text-4xl mt-6 text-[#1A1A1A]">
                  Browse machinery by category.
                </h2>
              </div>

              {/* Category filter */}
              <div
                data-testid="machinery-filter"
                className="flex flex-wrap gap-2"
              >
                {CATEGORIES.map((c) => (
                  <button
                    key={c}
                    data-testid={`machinery-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => setActive(c)}
                    className={`px-4 h-9 text-xs tracking-wider uppercase border transition-colors ${
                      active === c
                        ? "bg-[#1A1A1A] border-[#1A1A1A] text-white"
                        : "bg-white border-[#E5E5E5] text-[#2A2A2A] hover:border-[#D4AF37]"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>

            <div
              data-testid="machinery-grid"
              className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
            >
              {filtered.map((m) => {
                const Icon = ICONS[m.icon] || Cog;
                return (
                  <article
                    key={m.slug}
                    data-testid={`machinery-card-${m.slug}`}
                    className="group relative bg-white border border-[#E5E5E5] overflow-hidden flex flex-col hover:border-[#D4AF37] hover:-translate-y-0.5 transition-all"
                  >
                    {/* Visual */}
                    <div className="relative aspect-[4/3] bg-gradient-to-br from-[#F7F4EC] via-white to-[#FAFAFA] border-b border-[#E5E5E5] overflow-hidden">
                      {m.image ? (
                        <img
                          src={m.image}
                          alt={m.name}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Icon
                            className="w-14 h-14 text-[#D4AF37]/70"
                            strokeWidth={1.2}
                          />
                        </div>
                      )}
                      <span className="absolute top-3 left-3 text-[10px] tracking-[0.2em] uppercase text-[#1A1A1A] bg-white/95 backdrop-blur-sm border border-[#E5E5E5] px-2 py-1">
                        {m.category}
                      </span>
                      <span className="absolute bottom-3 right-3 inline-flex items-center justify-center w-9 h-9 bg-white/95 backdrop-blur-sm border border-[#E5E5E5]">
                        <Icon className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.6} />
                      </span>
                    </div>

                    <div className="p-6 md:p-7 flex flex-col flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <h3 className="font-serif-display text-xl md:text-2xl text-[#1A1A1A] leading-tight">
                          {m.name}
                        </h3>
                        <Wrench className="w-4 h-4 text-[#1A1A1A]/30 mt-1.5 flex-shrink-0" />
                      </div>

                      <p className="text-sm text-[#595959] mt-3 leading-relaxed">
                        {m.short}
                      </p>

                      <div className="mt-6 pt-5 border-t border-[#F0F0F0]">
                        <span className="text-[11px] tracking-[0.2em] uppercase text-[#8a8a8a]">
                          Specs on request
                        </span>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>

            {filtered.length === 0 && (
              <div className="text-center py-16 text-[#595959] border border-dashed border-[#E5E5E5]">
                No machinery in this category yet.
              </div>
            )}
          </div>
        </section>

        {/* Note + CTA */}
        <section className="pb-24 md:pb-32">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="grid md:grid-cols-2 gap-0 border border-[#E5E5E5]">
              <div className="bg-white p-8 md:p-12">
                <ShieldCheck
                  className="w-7 h-7 text-[#D4AF37]"
                  strokeWidth={1.4}
                />
                <h3 className="font-serif-display text-2xl md:text-3xl mt-5 text-[#1A1A1A] leading-tight">
                  Operated under
                  <br />
                  certified quality systems.
                </h3>
                <p className="text-[#595959] mt-4 leading-relaxed">
                  All equipment is run by trained operators following our
                  documented FSSAI, GMP and AYUSH procedures — from raw material
                  intake through finished pack.
                </p>
              </div>
              <div className="bg-[#1A1A1A] text-white p-8 md:p-12">
                <div className="overline" style={{ color: "#D4AF37" }}>
                  Detailed specifications
                </div>
                <p
                  data-testid="machinery-note"
                  className="mt-4 text-white/80 leading-relaxed"
                >
                  {MACHINERY_NOTE}
                </p>
                <button
                  data-testid="machinery-bottom-quote"
                  onClick={openQuote}
                  className="mt-6 btn-gold rounded-sm h-11 px-6 text-sm font-semibold tracking-wider inline-flex items-center gap-2"
                >
                  Request Specs <ArrowUpRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <StickyMobileCTA />
      <QuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} />
      <Toaster position="top-center" richColors />
    </div>
  );
};
